// Individual Visitor & User Activity -- the optional fields, in ONE place:
// the settings checkboxes, the table columns and the detail panel all read
// this list, so a checkbox and the column it controls can never drift apart.
// Keys match server_elakay individualActivitySettings.FIELD_KEYS.
import { elakayAutoText } from "../../../pageInside/dataElakay";

const tx = (key, fallback) => elakayAutoText(`h_ia_${key}`, fallback);

export const FIELD_GROUPS = [
  {
    key: "timeline",
    get label() { return tx("grpTimeline", "Visit timeline"); },
    fields: [
      { key: "firstSeen", get label() { return tx("fFirstSeen", "First Seen"); }, get help() { return tx("fFirstSeenHelp", "Date and time this browser was first recorded."); } },
      { key: "lastSeen", get label() { return tx("fLastSeen", "Last Seen"); }, get help() { return tx("fLastSeenHelp", "Date and time of the browser's latest recorded activity."); } },
    ],
  },
  {
    key: "counts",
    get label() { return tx("grpCounts", "Activity counts"); },
    fields: [
      { key: "totalVisits", get label() { return tx("fVisits", "Total Visits"); }, get help() { return tx("fVisitsHelp", "Tabs opened. A refresh is not a new visit."); } },
      { key: "totalSessions", get label() { return tx("fSessions", "Total Sessions"); }, get help() { return tx("fSessionsHelp", "Continuous activity periods, ended by logout or inactivity. Also controls the session list."); } },
      { key: "totalPageViews", get label() { return tx("fPageViews", "Total Page Views"); }, get help() { return tx("fPageViewsHelp", "Routes displayed, counted per visitor."); } },
      { key: "optionalMetrics", get label() { return tx("fOptional", "Optional Activity Metrics"); }, get help() { return tx("fOptionalHelp", "Active days, today's counters and the conservative traffic class (likely human / automated suspected / suspicious / unclassified)."); } },
    ],
  },
  {
    key: "auth",
    get label() { return tx("grpAuth", "Sign-in activity"); },
    fields: [
      { key: "loginCount", get label() { return tx("fLogins", "Login Count"); }, get help() { return tx("fLoginsHelp", "Successful sign-ins on this browser, last login and last logout."); } },
      { key: "failedLoginCount", get label() { return tx("fFailed", "Failed Login Count"); }, get help() { return tx("fFailedHelp", "Failed sign-in attempts on this browser. The typed e-mail is never stored here."); } },
    ],
  },
  {
    key: "location",
    get label() { return tx("grpLocation", "Approximate location"); },
    fields: [
      { key: "country", get label() { return tx("fCountry", "Country"); }, get help() { return tx("fCountryHelp", "Approximate, from the network (VPNs and mobile carriers can be wrong). Never GPS, never the IP address."); } },
      { key: "state", get label() { return tx("fState", "State"); }, get help() { return tx("fStateHelp", "Approximate state or region."); } },
      { key: "city", get label() { return tx("fCity", "City"); }, get help() { return tx("fCityHelp", "Approximate city."); } },
    ],
  },
  {
    key: "device",
    get label() { return tx("grpDevice", "Device"); },
    fields: [
      { key: "browser", get label() { return tx("fBrowser", "Browser"); }, get help() { return tx("fBrowserHelp", "Browser family only (Chrome, Safari…), not the full user agent."); } },
      { key: "operatingSystem", get label() { return tx("fOs", "Operating System"); }, get help() { return tx("fOsHelp", "Operating system family only (Windows, iOS…)."); } },
      { key: "deviceType", get label() { return tx("fDevice", "Device Type"); }, get help() { return tx("fDeviceHelp", "Desktop, Mobile, Tablet, or Bot when the browser declares itself automated."); } },
    ],
  },
  {
    key: "sources",
    get label() { return tx("grpSources", "Sources & history"); },
    fields: [
      { key: "referrer", get label() { return tx("fReferrer", "Referrer"); }, get help() { return tx("fReferrerHelp", "Website host and category (search, social, email, other) that brought the visitor. Never the full link."); } },
      { key: "historyPage", get label() { return tx("fHistorypage", "Historypage"); }, get help() { return tx("fHistorypageHelp", "[route, number] page views per route. Query strings, tokens and e-mail addresses are removed."); } },
      { key: "historyLog", get label() { return tx("fHistorylog", "Historylog"); }, get help() { return tx("fHistorylogHelp", "Day-by-day timeline: sessions, logins, logouts, failed attempts, inferred session ends."); } },
    ],
  },
];

export const FIELD_KEYS = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key));
export const fieldLabel = (key) => FIELD_GROUPS.flatMap((g) => g.fields).find((f) => f.key === key)?.label || key;

// Table columns. `fields`: shown when ANY of these checkboxes is on; none =
// always shown (identity, status and security come from mandatory data).
export const COLUMNS = [
  { key: "visitor", get label() { return tx("cVisitor", "Visitor"); } },
  { key: "account", get label() { return tx("cAccount", "Account"); } },
  { key: "status", get label() { return tx("cStatus", "Status"); } },
  { key: "firstSeen", fields: ["firstSeen"], get label() { return tx("fFirstSeen", "First Seen"); }, sort: "firstSeen" },
  { key: "lastSeen", fields: ["lastSeen"], get label() { return tx("fLastSeen", "Last Seen"); }, sort: "lastActivity" },
  { key: "location", fields: ["country", "state", "city"], get label() { return tx("cLocation", "Location"); } },
  { key: "device", fields: ["deviceType", "operatingSystem", "browser"], get label() { return tx("cDevice", "Device"); } },
  { key: "visits", fields: ["totalVisits"], get label() { return tx("cVisits", "Visits"); }, sort: "totalVisits", num: true },
  { key: "sessions", fields: ["totalSessions"], get label() { return tx("cSessions", "Sessions"); }, sort: "totalSessions", num: true },
  { key: "pageViews", fields: ["totalPageViews"], get label() { return tx("cPageViews", "Page views"); }, sort: "totalPageViews", num: true },
  { key: "logins", fields: ["loginCount"], get label() { return tx("cLogins", "Logins"); }, num: true },
  { key: "failed", fields: ["failedLoginCount"], get label() { return tx("cFailed", "Failed"); }, sort: "failedLogins", num: true },
  { key: "referrer", fields: ["referrer"], get label() { return tx("fReferrer", "Referrer"); } },
  { key: "traffic", fields: ["optionalMetrics"], get label() { return tx("cTraffic", "Traffic"); } },
  { key: "security", get label() { return tx("cSecurity", "Security"); } },
];

export const visibleColumns = (fields = {}) => COLUMNS.filter((c) => !c.fields || c.fields.some((f) => fields[f] !== false));

export const TRAFFIC_LABELS = {
  get likely_human() { return tx("tHuman", "Likely human"); },
  get automated_suspected() { return tx("tBot", "Automated (suspected)"); },
  get suspicious() { return tx("tSuspicious", "Suspicious"); },
  get unclassified() { return tx("tUnclassified", "Unclassified"); },
};

export const REFERRER_LABELS = {
  get search() { return tx("rSearch", "Search"); },
  get social() { return tx("rSocial", "Social"); },
  get email() { return tx("rEmail", "Email"); },
  get other_site() { return tx("rOther", "Other site"); },
};

export const EVENT_LABELS = {
  get session_start() { return tx("eSessionStart", "Session started"); },
  get session_end() { return tx("eSessionEnd", "Session ended"); },
  get login_success() { return tx("eLogin", "Signed in"); },
  get login_failed() { return tx("eLoginFailed", "Failed sign-in"); },
  get login_challenge_sent() { return tx("eChallenge", "New-device code sent"); },
  get login_blocked_suspended() { return tx("eBlocked", "Sign-in refused (suspended account)"); },
  get logout() { return tx("eLogout", "Signed out"); },
  get logout_all() { return tx("eLogoutAll", "Signed out of all devices"); },
  get authenticated_session_detected() { return tx("eDetected", "Signed-in session detected (no sign-in on this browser)"); },
  get signed_out_detected() { return tx("eSignedOutDetected", "Became signed out (without a logout request)"); },
  get session_token_rejected() { return tx("eTokenRejected", "Saved login no longer valid"); },
  get account_switch() { return tx("eSwitch", "Switched account"); },
  get rate_limited() { return tx("eRateLimited", "Too many requests (rate limited)"); },
  get account_link_added() { return tx("eLinkAdded", "Accounts associated by an administrator"); },
  get account_link_removed() { return tx("eLinkRemoved", "Account association removed"); },
  get security_event() { return tx("eSecurity", "Security event"); },
  get restriction() { return tx("eRestriction", "Account restriction"); },
};
